import { useEffect, useMemo, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { DOC_TYPES } from "../lib/docTypes";
import { ProgramSelect } from "./ProgramSelect";
import { Wand2, Info, ChevronDown, Loader2 } from "lucide-react";

const CUSTOM_LESSON = "__custom__";

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-semibold text-slate-700">{label}</Label>
    {children}
    {hint && <p className="text-xs text-slate-400 leading-snug">{hint}</p>}
  </div>
);

export const GeneratorForm = ({ docType, config, generating, onGenerate }) => {
  const dt = DOC_TYPES.find((d) => d.id === docType);
  const [level, setLevel] = useState("");
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [lesson, setLesson] = useState("");
  const [customLesson, setCustomLesson] = useState("");
  const [studentName, setStudentName] = useState("");
  const [studentDifficulties, setStudentDifficulties] = useState("");
  const [customTitle, setCustomTitle] = useState("");
  const [customDescription, setCustomDescription] = useState("");
  const [gikMode, setGikMode] = useState("mjeseci");
  const [activityName, setActivityName] = useState("");
  const [targetGrades, setTargetGrades] = useState("");
  const [hoursYear, setHoursYear] = useState("");
  const [destination, setDestination] = useState("");
  const [duration, setDuration] = useState("");
  const [meetingDate, setMeetingDate] = useState("");
  const [meetingTopics, setMeetingTopics] = useState("");
  const [teacherName, setTeacherName] = useState(localStorage.getItem("pk_teacher") || "");
  const [school, setSchool] = useState(localStorage.getItem("pk_school") || "");
  const [schoolYear, setSchoolYear] = useState("2026./2027.");
  const [extra, setExtra] = useState("");
  const [showOptional, setShowOptional] = useState(false);
  const programs = config?.programs?.[level]?.[subject]?.[grade] || [];
  const [program, setProgram] = useState("");

  useEffect(() => {
    setLesson("");
    setCustomLesson("");
  }, [level, grade, subject]);

  useEffect(() => {
    setProgram(programs[0] || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, grade, subject, config]);

  const levelObj = config?.levels.find((l) => l.id === level);
  const subjects = config?.subjects?.[level]?.[grade] || [];
  const supported = useMemo(
    () =>
      config?.supported.find(
        (s) => s.level === level && String(s.grade) === grade && s.subject === subject
      ),
    [config, level, grade, subject]
  );
  const selectionComplete = level && grade && subject;
  const isVlastiti = docType === "vlastiti";

  const finalLesson = lesson === CUSTOM_LESSON ? customLesson : lesson;
  let canGenerate;
  if (isVlastiti) {
    canGenerate = customTitle.trim() && customDescription.trim();
  } else if (docType === "ina") {
    canGenerate = activityName.trim();
  } else if (dt.needsSubject) {
    canGenerate = selectionComplete && supported && (!dt.needsLesson || finalLesson.trim());
  } else {
    canGenerate = level && grade;
    if (docType === "izvanucionicka") canGenerate = canGenerate && destination.trim();
    if (docType === "zapisnik_roditeljski") canGenerate = canGenerate && meetingTopics.trim();
  }

  const handleSubmit = () => {
    localStorage.setItem("pk_teacher", teacherName);
    localStorage.setItem("pk_school", school);
    onGenerate({
      doc_type: docType,
      level,
      grade: grade ? Number(grade) : 0,
      subject,
      lesson: finalLesson,
      teacher_name: teacherName,
      school,
      school_year: schoolYear,
      extra_instructions: extra,
      student_name: studentName,
      student_difficulties: studentDifficulties,
      custom_title: customTitle,
      custom_description: customDescription,
      gik_mode: gikMode,
      activity_name: activityName,
      target_grades: targetGrades,
      hours_year: hoursYear,
      destination,
      duration,
      meeting_date: meetingDate,
      meeting_topics: meetingTopics,
      program: dt.needsSubject ? program : "",
    });
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-6" data-testid="generator-form">
      <div>
        <h2 className="font-heading text-2xl font-bold text-slate-900">{dt.label}</h2>
        <p className="text-sm text-slate-500 mt-1">{dt.short}</p>
      </div>

      {dt.needsSubject && (
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Razina obrazovanja">
            <Select value={level} onValueChange={(v) => { setLevel(v); setGrade(""); setSubject(""); }}>
              <SelectTrigger data-testid="select-level" className="h-12 rounded-xl">
                <SelectValue placeholder="Odaberite razinu" />
              </SelectTrigger>
              <SelectContent>
                {config?.levels.map((l) => (
                  <SelectItem key={l.id} value={l.id} data-testid={`level-option-${l.id}`}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Razred">
            <Select value={grade} onValueChange={setGrade} disabled={!level}>
              <SelectTrigger data-testid="select-grade" className="h-12 rounded-xl">
                <SelectValue placeholder="Odaberite razred" />
              </SelectTrigger>
              <SelectContent>
                {(levelObj?.grades || []).map((g) => (
                  <SelectItem key={g} value={String(g)} data-testid={`grade-option-${g}`}>{g}. razred</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Predmet">
            <Select value={subject} onValueChange={setSubject} disabled={!level || !grade}>
              <SelectTrigger data-testid="select-subject" className="h-12 rounded-xl">
                <SelectValue placeholder={grade ? "Odaberite predmet" : "Prvo odaberite razred"} />
              </SelectTrigger>
              <SelectContent>
                {subjects.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
      )}

      {dt.needsLevelGrade && !dt.needsSubject && (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Razina obrazovanja">
            <Select value={level} onValueChange={(v) => { setLevel(v); setGrade(""); }}>
              <SelectTrigger data-testid="select-level" className="h-12 rounded-xl">
                <SelectValue placeholder="Odaberite razinu" />
              </SelectTrigger>
              <SelectContent>
                {config?.levels.map((l) => (
                  <SelectItem key={l.id} value={l.id} data-testid={`level-option-${l.id}`}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Razred">
            <Select value={grade} onValueChange={setGrade} disabled={!level}>
              <SelectTrigger data-testid="select-grade" className="h-12 rounded-xl">
                <SelectValue placeholder="Odaberite razred" />
              </SelectTrigger>
              <SelectContent>
                {(levelObj?.grades || []).map((g) => (
                  <SelectItem key={g} value={String(g)} data-testid={`grade-option-${g}`}>{g}. razred</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
      )}

      {dt.needsSubject && selectionComplete && !supported && (
        <div
          data-testid="unsupported-notice"
          className="flex items-start gap-3 rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800"
        >
          <Info className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Ova kombinacija još nije podržana.</p>
            <p className="mt-1">
              Podrška postoji za sve razrede i predmete <strong>osnovne škole, gimnazije i strukovnih škola</strong> u općeobrazovnim predmetima. Strukovni predmeti stižu uskoro.
            </p>
          </div>
        </div>
      )}

      {dt.needsSubject && supported && programs.length > 1 && (
        <ProgramSelect programs={programs} value={program} onChange={setProgram} />
      )}

      {dt.hasGikMode && supported && (
        <Field
          label="Način organizacije plana"
          hint="Odaberite kako želite da plan bude raspoređen u tablici."
        >
          <div className="grid sm:grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="gik-mode-mjeseci"
              onClick={() => setGikMode("mjeseci")}
              className={`rounded-xl border p-4 text-left transition-colors ${
                gikMode === "mjeseci"
                  ? "bg-blue-50 border-blue-400 text-blue-800"
                  : "bg-white border-slate-200 text-slate-700 hover:border-blue-200"
              }`}
            >
              <span className="font-semibold block">Po mjesecima</span>
              <span className="text-xs text-slate-500">Jedan redak tablice za svaki mjesec (rujan-lipanj)</span>
            </button>
            <button
              type="button"
              data-testid="gik-mode-sati"
              onClick={() => setGikMode("sati")}
              className={`rounded-xl border p-4 text-left transition-colors ${
                gikMode === "sati"
                  ? "bg-blue-50 border-blue-400 text-blue-800"
                  : "bg-white border-slate-200 text-slate-700 hover:border-blue-200"
              }`}
            >
              <span className="font-semibold block">Po nastavnim satima</span>
              <span className="text-xs text-slate-500">Redni broj za svaki nastavni sat (1., 2., 3. ...)</span>
            </button>
          </div>
        </Field>
      )}

      {dt.needsLesson && supported && (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Lekcija (nastavna jedinica)" hint="Odaberite s popisa ili upišite vlastiti naslov.">
            <Select value={lesson} onValueChange={setLesson}>
              <SelectTrigger data-testid="select-lesson" className="h-12 rounded-xl">
                <SelectValue placeholder="Odaberite lekciju" />
              </SelectTrigger>
              <SelectContent>
                {supported.lessons.map((l) => (
                  <SelectItem key={l} value={l}>{l}</SelectItem>
                ))}
                <SelectItem value={CUSTOM_LESSON} data-testid="lesson-custom-option">
                  ✏️ Upiši vlastiti naslov lekcije…
                </SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {lesson === CUSTOM_LESSON && (
            <Field label="Vlastiti naslov lekcije">
              <Input
                data-testid="input-custom-lesson"
                className="h-12 rounded-xl"
                placeholder="npr. Ponavljanje gradiva prije ispita"
                value={customLesson}
                onChange={(e) => setCustomLesson(e.target.value)}
              />
            </Field>
          )}
        </div>
      )}

      {dt.needsStudent && supported && (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Ime učenika (ili inicijali)" hint="Možete upisati samo inicijale radi privatnosti.">
            <Input
              data-testid="input-student-name"
              className="h-12 rounded-xl"
              placeholder="npr. I. H."
              value={studentName}
              onChange={(e) => setStudentName(e.target.value)}
            />
          </Field>
          <Field
            label="Opis teškoća učenika"
            hint="Svojim riječima, npr. 'sporije čita i piše, teško zadržava pažnju'."
          >
            <Textarea
              data-testid="input-student-difficulties"
              className="rounded-xl min-h-12"
              rows={2}
              placeholder="Opišite teškoće i osobitosti učenika…"
              value={studentDifficulties}
              onChange={(e) => setStudentDifficulties(e.target.value)}
            />
          </Field>
        </div>
      )}

      {docType === "ina" && (
        <div className="space-y-4">
          <Field label="Naziv izvannastavne aktivnosti" hint="npr. Dramska skupina, Mali nogomet, Novinarska grupa, Eko grupa…">
            <Input
              data-testid="input-activity-name"
              className="h-12 rounded-xl"
              placeholder="Kako se aktivnost zove?"
              value={activityName}
              onChange={(e) => setActivityName(e.target.value)}
            />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Kojim razredima je namijenjena" hint="Nije obavezno.">
              <Input
                data-testid="input-target-grades"
                className="h-12 rounded-xl"
                placeholder="npr. 5. - 8. razred"
                value={targetGrades}
                onChange={(e) => setTargetGrades(e.target.value)}
              />
            </Field>
            <Field label="Broj sati godišnje" hint="Ako ne upišete, koristi se 35 sati (1 sat tjedno).">
              <Input
                data-testid="input-hours-year"
                className="h-12 rounded-xl"
                placeholder="npr. 35"
                value={hoursYear}
                onChange={(e) => setHoursYear(e.target.value)}
              />
            </Field>
          </div>
        </div>
      )}

      {docType === "izvanucionicka" && level && grade && (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Odredište i tema" hint="npr. 'Vukovar, Dan sjećanja' ili 'Tehnički muzej Nikola Tesla, Zagreb'.">
            <Input
              data-testid="input-destination"
              className="h-12 rounded-xl"
              placeholder="Kamo idete i zašto?"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
            />
          </Field>
          <Field label="Trajanje" hint="Nije obavezno, npr. poludnevna, jednodnevna, dvodnevna…">
            <Input
              data-testid="input-duration"
              className="h-12 rounded-xl"
              placeholder="npr. jednodnevna"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
            />
          </Field>
        </div>
      )}

      {docType === "zapisnik_roditeljski" && level && grade && (
        <div className="space-y-4">
          <Field
            label="Teme sastanka (dnevni red)"
            hint="Napišite svojim riječima o čemu se razgovaralo, npr. 'uspjeh na kraju 1. polugodišta, ponašanje na odmorima, organizacija maturalca'."
          >
            <Textarea
              data-testid="input-meeting-topics"
              className="rounded-xl"
              rows={3}
              placeholder="O kojim ste temama razgovarali?"
              value={meetingTopics}
              onChange={(e) => setMeetingTopics(e.target.value)}
            />
          </Field>
          <Field label="Datum sastanka" hint="Nije obavezno.">
            <Input
              data-testid="input-meeting-date"
              className="h-12 rounded-xl"
              placeholder="npr. 15. 12. 2026."
              value={meetingDate}
              onChange={(e) => setMeetingDate(e.target.value)}
            />
          </Field>
        </div>
      )}

      {isVlastiti && (
        <div className="space-y-4">
          <Field label="Naziv dokumenta" hint="npr. Plan dopunske nastave, Izvješće o radu razrednika, Zapisnik roditeljskog sastanka…">
            <Input
              data-testid="input-custom-title"
              className="h-12 rounded-xl"
              placeholder="Kako se dokument zove?"
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
            />
          </Field>
          <Field
            label="Opišite što vam treba"
            hint="Pišite svojim riječima, kao da objašnjavate kolegici. Što više detalja napišete, bolji će biti dokument."
          >
            <Textarea
              data-testid="input-custom-description"
              className="rounded-xl"
              rows={4}
              placeholder="npr. Trebam plan dopunske nastave iz matematike za 5. razred, dva sata mjesečno, za učenike koji teže savladavaju razlomke…"
              value={customDescription}
              onChange={(e) => setCustomDescription(e.target.value)}
            />
          </Field>
        </div>
      )}

      <div className="border border-slate-200 rounded-2xl overflow-hidden">
        <button
          data-testid="toggle-optional"
          onClick={() => setShowOptional(!showOptional)}
          className="w-full flex items-center justify-between px-5 py-3.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
        >
          <span>Dodatni podaci i upute <span className="font-normal text-slate-400">(nije obavezno)</span></span>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showOptional ? "rotate-180" : ""}`} />
        </button>
        {showOptional && (
          <div className="px-5 pb-5 pt-1 space-y-4 border-t border-slate-100">
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Vaše ime i prezime">
                <Input data-testid="input-teacher-name" className="h-12 rounded-xl" placeholder="npr. Ana Anić" value={teacherName} onChange={(e) => setTeacherName(e.target.value)} />
              </Field>
              <Field label="Naziv škole">
                <Input data-testid="input-school" className="h-12 rounded-xl" placeholder="npr. OŠ Augusta Šenoe" value={school} onChange={(e) => setSchool(e.target.value)} />
              </Field>
              <Field label="Školska godina">
                <Input data-testid="input-school-year" className="h-12 rounded-xl" value={schoolYear} onChange={(e) => setSchoolYear(e.target.value)} />
              </Field>
            </div>
            <Field
              label="Dodatne upute za pripremu"
              hint="Napišite svojim riječima što želite, npr. 'Želim više grupnog rada', 'Učenici vole igre i kvizove', 'Neka sat bude vezan uz Advent'. Ne morate ništa posebno znati, pišite kao da pričate s kolegicom."
            >
              <Textarea
                data-testid="input-extra-instructions"
                className="rounded-xl"
                rows={3}
                placeholder="Vaše želje i upute…"
                value={extra}
                onChange={(e) => setExtra(e.target.value)}
              />
            </Field>
          </div>
        )}
      </div>

      <Button
        data-testid="generate-btn"
        onClick={handleSubmit}
        disabled={!canGenerate || generating}
        className="w-full h-14 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-base font-bold shadow-lg shadow-blue-600/25 transition-colors"
      >
        {generating ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            AI priprema vaš dokument…
          </>
        ) : (
          <>
            <Wand2 className="w-5 h-5 mr-2" />
            Izradi dokument
          </>
        )}
      </Button>
    </div>
  );
};
